import React from "react";
import { useTranslation } from "react-i18next";
import { BriefDetails } from "src/model/model";

interface QuizzDetailsProps {
  data: BriefDetails | Record<string, unknown>;
  mode: "plan" | "recap";
}

const ns = "pages.chat.interactionDetails";

/** Shared renderer for Quizz Text, Yes/No Question, Random Quizz */
const QuizzDetails: React.FC<QuizzDetailsProps> = ({ data, mode }) => {
  const { t } = useTranslation();
  if (!data) return null;

  if (mode === "plan") {
    const bd = data as BriefDetails;
    const hasContent =
      bd.expectedQuestion || bd.expectedCorrectAnswer || bd.expectedAnswerOptions?.length;
    if (!hasContent) return null;

    return (
      <div className="space-y-1 text-[11px] text-gray-700">
        {bd.expectedQuestion && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.question`)} : </span>
            {bd.expectedQuestion}
          </div>
        )}
        {bd.expectedAnswerOptions && bd.expectedAnswerOptions.length > 0 && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} : </span>
            {bd.expectedAnswerOptions.join(" · ")}
          </div>
        )}
        {bd.expectedCorrectAnswer && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.correctAnswer`)} : </span>
            <span className="text-green-700">{bd.expectedCorrectAnswer}</span>
          </div>
        )}
        {bd.expectedDifficulty && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.difficulty`)} : </span>
            {bd.expectedDifficulty}
          </div>
        )}
      </div>
    );
  }

  // Recap mode: real generated content
  const d = data as Record<string, unknown>;
  return (
    <div className="space-y-1 text-[11px] text-gray-700">
      {d.question_text && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.question`)} : </span>
          {String(d.question_text)}
        </div>
      )}
      {d.question && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.question`)} : </span>
          {String(d.question)}
        </div>
      )}
      {/* Quizz Text single-choice (quizz-1) */}
      {d.answer_1_text && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} :</span>
          <ul className="ml-3 mt-0.5 list-disc list-inside">
            {[1, 2, 3].map((n) => {
              const text = d[`answer_${n}_text`];
              const isCorrect = d[`is_answer_${n}_true`];
              if (!text) return null;
              return (
                <li key={n} className={isCorrect ? "text-green-700" : "text-gray-600"}>
                  {String(text)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* Quizz Text multi-choice (quizz-3) */}
      {Array.isArray(d.list_answers) && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} :</span>
          <ul className="ml-3 mt-0.5 list-disc list-inside">
            {(d.list_answers as string[]).map((ans, i) => {
              const isCorrect = Array.isArray(d.correct_answers)
                ? (d.correct_answers as boolean[])[i]
                : false;
              return (
                <li key={i} className={isCorrect ? "text-green-700" : "text-gray-600"}>
                  {ans}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* Quizz Sort */}
      {Array.isArray(d.answers) && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} : </span>
          {(d.answers as Array<{ text?: string; index?: number }>)
            .sort((a, b) => (a.index || 0) - (b.index || 0))
            .map((a) => a.text || "")
            .join(" → ")}
        </div>
      )}
      {d.explanation_true_text && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.explanationCorrect`)} : </span>
          {String(d.explanation_true_text)}
        </div>
      )}
    </div>
  );
};

export default QuizzDetails;
